/**
 * MiniPlayer Component - Persistent player at bottom
 * Includes playback effects (Step 8)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, SkipForward, Gauge, Zap, Music2 } from 'lucide-react';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import { formatTime, truncate } from '../utils/helpers';
import ExpandedPlayer from './ExpandedPlayer';

const EFFECTS = [
  { id: 'normal', icon: Music2, rate: 1.0, label: '1x' },
  { id: 'chopped', icon: Gauge, rate: 0.8, label: '0.8x' },
  { id: 'sped_up', icon: Zap, rate: 1.25, label: '1.25x' }
];

const MiniPlayer = () => {
  const { 
    currentTrack, 
    isPlaying, 
    togglePlayPause, 
    handleNext,
    currentTime,
    duration,
    setSpeed
  } = useAudioPlayer();

  const [currentEffect, setCurrentEffect] = useState('normal');
  const [showEffects, setShowEffects] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Apply playback rate when effect changes
  useEffect(() => {
    const effect = EFFECTS.find(e => e.id === currentEffect);
    if (effect && setSpeed) {
      setSpeed(effect.rate);
    }
  }, [currentEffect, setSpeed]);

  if (!currentTrack) return null;

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const currentEffectData = EFFECTS.find(e => e.id === currentEffect);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-16 left-0 right-0 z-40 px-4 pb-2"
        data-testid="mini-player"
      >
        {/* Effects Panel */}
        <AnimatePresence>
          {showEffects && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="mb-2 bg-[#12101A]/95 backdrop-blur-xl rounded-xl border border-white/10 p-2 flex gap-2"
            >
              {EFFECTS.map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  onClick={() => { setCurrentEffect(id); setShowEffects(false); }}
                  className={`flex-1 py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                    currentEffect === id 
                      ? 'bg-[#00F0FF]/20 text-[#00F0FF]' 
                      : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10 hover:text-white'
                  }`}
                  data-testid={`effect-${id}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-xs font-medium">{label}</span>
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="bg-[#12101A]/95 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
          {/* Progress Bar */}
          <div className="h-0.5 bg-white/10">
            <motion.div 
              className="h-full bg-[#00F0FF]"
              style={{ width: `${progress}%` }}
              transition={{ duration: 0.1 }}
            />
          </div>

          <div className="flex items-center gap-3 p-3">
            {/* Artwork + info — tap to open the full player */}
            <button
              onClick={() => setExpanded(true)}
              className="flex items-center gap-3 flex-1 min-w-0 text-left"
              data-testid="mini-player-expand"
            >
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#1a1625] flex-shrink-0">
                {currentTrack.artwork ? (
                  <img src={currentTrack.artwork} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#00F0FF] to-[#FF007F]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium text-sm truncate">
                  {truncate(currentTrack.track_title, 25)}
                </p>
                <p className="text-[#A1A1AA] text-xs truncate">
                  {currentTrack.artist_name}
                </p>
              </div>
            </button>

            {/* Time */}
            <span className="text-[#52525B] text-xs tabular-nums">
              {formatTime(currentTime)}
            </span>

            {/* Playback Effect Button */}
            <button
              onClick={() => setShowEffects(!showEffects)}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                currentEffect !== 'normal' 
                  ? 'bg-[#00F0FF]/20 text-[#00F0FF]' 
                  : 'bg-white/10 text-[#A1A1AA] hover:bg-white/20'
              }`}
              title="Playback Speed"
              data-testid="effect-toggle"
            >
              {currentEffectData && <currentEffectData.icon className="w-4 h-4" />}
            </button>

            {/* Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={togglePlayPause}
                className="w-10 h-10 rounded-full bg-[#00F0FF] flex items-center justify-center hover:bg-[#00F0FF]/90 transition-colors"
                data-testid="mini-player-play"
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5 text-[#050505]" />
                ) : (
                  <Play className="w-5 h-5 text-[#050505] ml-0.5" />
                )}
              </button>
              <button
                onClick={handleNext}
                className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                data-testid="mini-player-next"
              >
                <SkipForward className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
      <ExpandedPlayer open={expanded} onClose={() => setExpanded(false)} />
    </AnimatePresence>
  );
};

export default MiniPlayer;
